/**
 * User Activity Tracking - تتبع أنشطة المستخدمين
 */
import { API } from '@/config';
import i18n from 'i18next';

const getUser = () => {
  try {
    return JSON.parse(localStorage.getItem('user') || '{}');
  } catch { return {}; }
};

/**
 * Check if tracking should be blocked (admin users or non-production environments)
 */
const shouldBlockTracking = () => {
  // Block in non-production environments (preview, localhost, etc.)
  const hostname = window.location.hostname;
  const isProduction = hostname === 'drivemaster.se' || hostname === 'www.drivemaster.se';
  if (!isProduction) return true;

  // Block admin/super_admin users
  const user = getUser();
  if (user.is_admin) return true;
  if (user.role === 'super_admin' || user.role === 'admin') return true;

  return false;
};

const getVisitorId = () => {
  let vid = localStorage.getItem('dm_visitor_id');
  if (!vid) {
    vid = 'v_' + Math.random().toString(36).substr(2, 9) + Date.now().toString(36);
    localStorage.setItem('dm_visitor_id', vid);
  }
  return vid;
};

/**
 * Track any user action
 */
export const trackActivity = async (action, details = '', page = null) => {
  if (shouldBlockTracking()) return;
  try {
    const user = getUser();
    await fetch(`${API}/analytics/track-action`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        action,
        page: page || window.location.pathname,
        details,
        user_id: user.id || null,
        user_email: user.email || null,
        user_name: user.name || null,
        visitor_id: getVisitorId(),
        language: i18n.language || 'sv',
        referrer: document.referrer || ''
      })
    });
  } catch { /* silent */ }
};

// Track page view
export const trackPageView = (page) => {
  const key = page || window.location.pathname;
  trackActivity('page_view', '', key);
};

// Convenience functions
export const trackSubscribeClick = (planName, planPrice) => trackActivity('subscribe_click', `${planName} - ${planPrice} SEK`);
export const trackTestStart = (testName) => trackActivity('test_start', testName);
export const trackTestComplete = (testName, score) => trackActivity('test_complete', `${testName} - ${score}%`);
export const trackLogin = (method) => trackActivity('login', method);
export const trackRegister = (method) => trackActivity('register', method);
export const trackLanguageChange = (lang) => trackActivity('language_change', lang);
export const trackSignView = (signName) => trackActivity('sign_view', signName);
export const trackLessonView = (lessonName) => trackActivity('lesson_view', lessonName);
export const trackAIChat = () => trackActivity('ai_chat', '');
export const trackShareResult = (platform) => trackActivity('share_result', platform);
export const trackSpeedChallenge = (score) => trackActivity('speed_challenge', `Score: ${score}`);
export const trackPaywallShown = (page) => trackActivity('paywall_shown', '', page);
export const trackPaywallDismissed = (page) => trackActivity('paywall_dismissed', '', page);
export const trackTestQuestionView = (testName, qNum) => trackActivity('test_question_view', `${testName} Q${qNum}`);
export const trackTestAbandoned = (testName, qNum, total) => trackActivity('test_abandoned', `${testName} - ${qNum}/${total}`);
export const trackPricingView = () => trackActivity('pricing_view', '');

// Page time tracking
let pageEntryTime = Date.now();
let currentPagePath = '';

export const trackPageEntry = (page) => {
  if (currentPagePath && currentPagePath !== page) {
    const timeSpent = Math.round((Date.now() - pageEntryTime) / 1000);
    if (timeSpent > 2 && timeSpent < 3600) {
      trackActivity('page_exit', `${timeSpent}s`, currentPagePath);
    }
  }
  currentPagePath = page || window.location.pathname;
  pageEntryTime = Date.now();
};

// Track exit on page unload
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    if (shouldBlockTracking()) return;
    const timeSpent = Math.round((Date.now() - pageEntryTime) / 1000);
    if (timeSpent > 2 && currentPagePath) {
      const data = JSON.stringify({
        action: 'session_end',
        page: currentPagePath,
        details: `${timeSpent}s`,
        visitor_id: getVisitorId(),
        language: i18n.language || 'sv'
      });
      navigator.sendBeacon?.(`${API}/analytics/track-action`, data);
    }
  });
}

// Error tracking
export const initErrorTracking = () => {
  window.addEventListener('error', (event) => {
    if (!shouldBlockTracking()) trackActivity('js_error', event.message);
  });
  window.addEventListener('unhandledrejection', (event) => {
    if (!shouldBlockTracking()) trackActivity('promise_error', event.reason?.message || 'Unhandled');
  });
};

// Legacy exports for backward compatibility
export const trackPageVisit = trackPageView;
export const trackAction = trackActivity;
export const trackError = (type, msg) => trackActivity('error', `${type}: ${msg}`);
export const AnalyticsEvents = {
  LOGIN: 'login', LOGOUT: 'logout', REGISTER: 'register',
  START_TEST: 'test_start', SUBMIT_TEST: 'test_complete',
  VIEW_LESSON: 'lesson_view', VIEW_SIGN: 'sign_view',
  VIEW_PRICING: 'view_pricing', START_SUBSCRIPTION: 'subscribe_click',
  SEND_MESSAGE: 'ai_chat'
};

export default { trackActivity, trackPageView, initErrorTracking };
